import { createContext, useContext, useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'
import { setUser as setAnalyticsUser } from '../lib/analytics'
import { setSentryUser } from '../lib/sentry'

const AuthContext = createContext(null)

export function AuthProvider({ children }) {
  const [user, setUser]       = useState(null)
  const [profile, setProfile] = useState(null)
  const [loading, setLoading] = useState(true)

  async function loadProfile(userId) {
    const { data } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single()
    setProfile(data)
    if (data) {
      setAnalyticsUser(data.id, {
        role: data.role,
        plan: data.subscription_plan,
        subscription_status: data.subscription_status,
      })
      setSentryUser({
        id: data.id,
        email: data.email,
        full_name: data.full_name,
        role: data.role,
      })
    }
  }

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null)
      if (session?.user) loadProfile(session.user.id).finally(() => setLoading(false))
      else setLoading(false)
    })

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      // TOKEN_REFRESHED and SIGNED_IN can fire silently when switching tabs
      // (Supabase calls _recoverAndRefresh on visibilitychange).
      // Only re-render if the user identity actually changed — otherwise the
      // app re-mounts and unsaved work is lost.
      if (event === 'TOKEN_REFRESHED') return
      if (event === 'SIGNED_IN' && session?.user) {
        // Update the access token silently but skip the loading spinner
        // if we already have a profile for the same user.
        setUser(prev => {
          if (prev?.id === session.user.id) return prev   // same user — no re-render
          return session.user
        })
        setProfile(prev => {
          if (prev?.id === session.user.id) return prev   // same profile — no re-render
          // Different user: reload profile with loading state
          setLoading(true)
          loadProfile(session.user.id).finally(() => setLoading(false))
          return prev
        })
        return
      }

      // SIGNED_OUT or other events: update normally
      setUser(session?.user ?? null)
      if (session?.user) {
        setLoading(true)
        loadProfile(session.user.id).finally(() => setLoading(false))
      } else {
        setProfile(null)
        setLoading(false)
      }
    })
    return () => subscription.unsubscribe()
  }, [])

  async function signIn(email, password) {
    const { error } = await supabase.auth.signInWithPassword({ email, password })
    if (error) throw error
  }

  async function signOut() {
    await supabase.auth.signOut()
    setUser(null)
    setProfile(null)
    setAnalyticsUser(null)
    setSentryUser(null)
  }

  const isAdmin      = profile?.role === 'admin'
  const isNVC        = profile?.role === 'admin' || profile?.role === 'consultant'
  const isClient     = profile?.role === 'client'
  const isCaja       = profile?.role === 'caja_user'
  const isClientCeo  = profile?.role === 'client_ceo'
  // Read-only is the gate we use to hide edit/save/delete in the section
  // pages. CEO is read-only; everyone else (including caja) writes.
  const isReadOnly   = isClientCeo

  return (
    <AuthContext.Provider value={{ user, profile, loading, signIn, signOut, isAdmin, isNVC, isClient, isCaja, isClientCeo, isReadOnly }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  return useContext(AuthContext)
}
